import { TApiTaskDetails } from "@timenotes/shared/src/services/api-client/types"
import createSimpleContext from "@timenotes/shared/src/utils/hooks/create-simple-context"
import { Modal } from "antd"
import { useEffect, useState } from "react"
import { TaskDetailsModalBody } from "./task-details-modal-body"
import { TaskDetailsModalTitle } from "./task-details-modal-title"
import styled from "styled-components"
import { useQueryClient } from "react-query"
import { useGetTaskQuery } from "@timenotes/shared/src/services/tasks/queries/use-get-task-query"
import { useDisableDocumentScroll } from "utils/use-disable-document-scroll"

interface TaskDetailsModalProps extends React.ComponentProps<typeof Modal> {
  projectId?: string
  taskId?: string
}

const TaskDetailsModal = (props: TaskDetailsModalProps) => {
  const {
    taskId,
    projectId,
    ...modalProps
  } = props

  const {
    getTaskQuery
  } = useGetTaskQuery({
    projectId,
    taskId,
  })

  const task = getTaskQuery.data?.task

  const {
    closeTaskModal,
    timesheetVisible,
  } = useTaskDetailsModal()

  const visible = !!modalProps.visible && !!task

  useDisableDocumentScroll(visible)
  
  return (
    <EditModal
      visible={visible}
      {...modalProps}
      title={task && (
        <TaskDetailsModalTitle task={task as TApiTaskDetails} />
      )}
      destroyOnClose={true}
      footer={null}
      onCancel={closeTaskModal}

      width={1100}
      style={{
        // Handle opening timesheet modal on top of this modal
        opacity: timesheetVisible ? 0 : 1,
      }}
    >
      {task && (
        <TaskDetailsModalBody
          task={task}
        />
      )}
    </EditModal>
  )
}

const useTaskDetailsModalInternal = () => {
  const [projectId, setProjectId] = useState<undefined | string>()
  const [taskId, setTaskId] = useState<undefined | string>()

  const [timesheetVisible, setTimesheetVisible] = useState(false)

  const [taskDetailsModalVisible, setTaskDetailsModalVisible] = useState(false)

  const queryClient = useQueryClient()

  const openTaskModal = ({ taskId, projectId }: {
    taskId: string,
    projectId: string,
  }) => {
    setProjectId(projectId)
    setTaskId(taskId)
    setTaskDetailsModalVisible(true)
  }

  const closeTaskModal = () => {
    setTaskDetailsModalVisible(false)
    setTaskId(undefined)
    setProjectId(undefined)
  }

  const openTaskTimesheet = () => {
    setTimesheetVisible(true)
  }

  const closeTaskTimesheet = () => {
    setTimesheetVisible(false)
    queryClient.invalidateQueries(['project-task', projectId, taskId])
  }

  return {
    openTaskModal,
    closeTaskModal,
    taskId,
    setTaskId,
    projectId,
    setProjectId,
    timesheetVisible,
    openTaskTimesheet,
    closeTaskTimesheet,
    taskDetailsModalVisible,
  }
}

export const {
  useValue: useTaskDetailsModal,
  withProvider: withTaskDetailsModalProvider,
} = createSimpleContext(useTaskDetailsModalInternal)

export const TaskDetailsModalProvider = withTaskDetailsModalProvider((props: React.PropsWithChildren) => {
  const {
    projectId,
    taskId,
    taskDetailsModalVisible,
  } = useTaskDetailsModal()

  return (
    <>
      {props.children}

      <TaskDetailsModal 
        visible={taskDetailsModalVisible}
        taskId={taskId}
        projectId={projectId}
      />
    </>
  )
})

export const EditModal = styled(Modal)`
  border-radius: 0.625rem;

  .ant-col-20 {
    max-width: 100%;
  }

  .ant-tabs-nav {
    margin-bottom: 0;
  }

  .ant-modal-header button:hover {
    color: #5b697a;
  }

  .ant-modal-header button {
    color: #72849a;
    font-weight: 700;
    cursor: pointer;
    font-size: 1.5rem;
    transform: translateY(-0.125rem);
    transition: 250ms;
  }

  .ant-modal-body {
    padding: 0;
  }

  .ant-tabs-nav-list {
    padding-left: 24px;
  }

  .ant-modal-header {
    padding: 15px;
    padding-left: 20px;
  }

  .ant-select-selector {
    width: 100%;
  }
`
